import { VdFeatureManager } from '@vend/vend.js'
import angular from 'angular'
import each from 'lodash/each'
import find from 'lodash/find'
import some from 'lodash/some'
import { vn, add, round } from 'vend-number'

import { CASH_MOVEMENT_TYPES } from '../../constants'
import {
  isCashConcealedPaymentType,
  isReadOnlyPaymentType,
} from '../../functions/payments'
import { isEnabledPaymentTypeForOutlet } from '../../functions/payment-types/payment-types'
import template from '../../../templates/register-closure/wr-register-closure-summary-table.html'
import { $log, $q } from '../../ng-compat/imports'
import { RegisterManager } from '../../managers/register-manager'
import { RegisterClosureManager } from '../../managers/register-closure-manager'
import { CashMovementsManager } from '../../managers/cash-movements-manager'
import { PaymentManager } from '../../managers/payment-manager'
import type { RegisterModals } from '../../modals/register-modals'

/**
 * The table summary of a register closure. Can be editable and used within a form by setting the `editable`
 * attribute to true.
 *
 * @module webregister.directives.wrRegisterClosureSummaryTable
 */
angular
  .module('webregister.directives')
  .directive('wrRegisterClosureSummaryTable', WrRegisterClosureSummaryTable)

function WrRegisterClosureSummaryTable(
  cashMovementsManager: CashMovementsManager,
  registerManager: RegisterManager,
  paymentManager: PaymentManager,
  registerClosureManager: RegisterClosureManager,
  registerModals: RegisterModals,
  vdFeatureManager: VdFeatureManager
) {
  return {
    scope: {
      registerClosure: '=',
      editable: '=',
      closeRegister: '&',
    },
    restrict: 'E',
    template,
    controllerAs: 'registerClosureSummaryTableCtrl',
    bindToController: true,

    /**
     * =========================================
     * REGISTER CLOSURE SUMMARY TABLE CONTROLLER
     * =========================================
     */
    controller(this: $TSFixMe) {
      const DECIMAL_PLACES = 2
      const ENTER_KEY_CODE = 13
      this.paymentManager = paymentManager
      this.isCashConcealedPaymentType = isCashConcealedPaymentType
      this.isReadOnlyPaymentType = isReadOnlyPaymentType
      this.registerClosureManager = registerClosureManager
      this.summaryTotals = {}
      this.hasCashConcealedPaymentType = false

      /**
       * Listens for changes in countedCashInDrawer and calculates the total counted amount for the associated
       * cash management payment type id.
       *
       * If counted cash in drawer does not match the expected cash in drawer, any differences are +/- from the
       * cash sales total. Otherwise the totalCounted is the total cash sales.
       *
       * @method  setTotalCountedForCashManagementPaymentType
       */
      this.setTotalCountedForCashManagementPaymentType = function () {
        if (this.registerClosure.countedCashInDrawer) {
          const { countedCashInDrawer } = this.registerClosure
          const {
            cashDrawerBalance,
            cash_sales_total: cashSalesTotal,
          } = this.registerClosure.cashDrawerTotals

          // Compare counted cash in drawer with expected cash drawer balance
          if (vn(countedCashInDrawer).cmp(cashDrawerBalance) !== 0) {
            const drawerDifference = this.registerClosureManager.differenceBetweenExpectedAndCounted(
              cashDrawerBalance,
              countedCashInDrawer
            )
            this.cashManagementPayment.totalCounted = round(
              add(cashSalesTotal, drawerDifference),
              DECIMAL_PLACES
            )
          } else {
            this.cashManagementPayment.totalCounted = round(
              cashSalesTotal,
              DECIMAL_PLACES
            )
          }
          $log.debug(
            `[REGISTER CLOSURE SUMMARY TABLE] Total Cash Counted: ${this.cashManagementPayment.totalCounted}`
          )
        }
        this.determineSummaryTotals()
      }

      /**
       * Key handler for pressing 'Enter' in input box
       * Will show Confirm Modal only when 'Enter' is pressed and all inputs are valid
       *
       * @method  keyPressHandler
       *
       * @param {Event} $event - The keyPress event
       */
      this.keyPressHandler = function ($event: KeyboardEvent) {
        const keyPressed = $event.keyCode

        // Only prevent 'Enter' key to submit the form when there is no error input and 'Enter' key is pressed.
        if (this.summaryTotals.countedValid && keyPressed === ENTER_KEY_CODE) {
          $event.preventDefault()
          registerModals
            .closeRegisterConfirmation()
            .then(() => this.closeRegister())
        }
      }

      /**
       * Updates cashMovements and cashDrawerTotals in registerClosure and updates the cash drawer summary table.
       *
       * @method updateCashMovementsAndTotals
       */
      this.updateCashMovementsAndTotals = function () {
        $q.all({
          cashMovements: cashMovementsManager.getCashMovements(),
          cashDrawerTotals: cashMovementsManager.getCashDrawerTotals(),
        }).then(data => {
          this.registerClosure.cashMovements = data.cashMovements
          this.registerClosure.cashDrawerTotals = data.cashDrawerTotals
          this._prepareCashDrawerSummaryForCashManagement()
        })
      }

      /**
       * Toggle the sales history row expanded state
       *
       * @method toggleRowClick
       *
       * @param {Event} The click event
       */
      this.toggleRowClick = ($event: $TSFixMe, payment: $TSFixMe) => {
        // Only want to expand the row for cash management payment that is not cash concealed,
        // so return on any other.
        if (!this.shouldExpandCashManagementRow(payment)) {
          return
        }

        // avoid expanding the row if the user clicks on the input
        if (!angular.element($event.target).hasClass('vd-input')) {
          angular
            .element($event.currentTarget)
            .toggleClass('vd-expandable--expanded')
        }
      }

      /**
       * Determine the register closure summary totals
       *
       * @method determineSummaryTotals
       */
      this.determineSummaryTotals = function () {
        this.summaryTotals = registerClosureManager.determineSummaryTotals(
          this.registerClosure,
          this.hasCashConcealedPaymentType
        )
      }

      /**
       * Determine whether the cash payment row should be expandable.
       *
       * @method shouldExpandCashManagementRow
       */
      this.shouldExpandCashManagementRow = function (payment: $TSFixMe) {
        return (
          this.paymentManager.isCashManagementPaymentType(payment) &&
          !this.isCashConcealedPaymentType(payment)
        )
      }

      /**
       * Determine the default total counted value for the given payment type. Defaults to 0 if the expected
       * total is 0, otherwise left blank/undefined.
       *
       * @private
       * @method _defaultPaymentTotalCounted
       *
       * @param  {Payment} payment
       *         The payment to default the counted value for
       */
      this._defaultPaymentTotalCounted = function (payment: $TSFixMe) {
        if (payment.isSystemPaymentType || isReadOnlyPaymentType(payment)) {
          payment.totalCounted = payment.total
        } else if (
          angular.isUndefined(payment.totalCounted) &&
          parseFloat(payment.total) === 0.0
        ) {
          payment.totalCounted = '0'
        }
      }

      /**
       * Responsible for preparing the cash drawer summary for cash management. Only initiated when cash
       * management is enabled on the register.
       *
       * @private
       * @method _prepareCashDrawerSummaryForCashManagement
       */
      this._prepareCashDrawerSummaryForCashManagement = function () {
        const cashManagementCashFloat = find(
          this.registerClosure.cashMovements,
          { type: CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT }
        )
        this.isCashFloatSet = Boolean(cashManagementCashFloat)
        this.registerClosure.openingCashFloat =
          (cashManagementCashFloat && cashManagementCashFloat.amount) || 0
        this.cashManagementPaymentTypeId = cashMovementsManager.getCashManagementPaymentTypeId()
        this.cashManagementPayment = find(this.registerClosure.payments, {
          payment_type_id: this.cashManagementPaymentTypeId,
        })
      }

      /**
       * Filters a collection of payments so they only include those allowed for the given outlet.
       * Only payments which have a payment type which is enabled for this outlet are allowed through the filter.
       * If the payment had a sale, regardless if it is disabled for the outlet or not, it is allowed through the filter.
       * This filtering is done to simplify the register closure screen to only include payments relevant to the current outlet.
       *
       * @private
       * @method _filterPayments
       *
       * @param payments
       *        The collection of payments to be filtered.
       * @param outletId
       *        The id of the outlet to filter by.
       *
       * @return A filtered collection of payments relevant to the given outlet.
       */
      this._filterPayments = function (payments: $TSFixMe, outletId: $TSFixMe) {
        return payments.filter((payment: $TSFixMe) => {
          // If there was a sale with this payment type it should be shown
          if (payment.total > 0) {
            return true
          }

          // Otherwise we check to see if it's enabled for this outlet
          return isEnabledPaymentTypeForOutlet(payment.paymentType, outletId)
        })
      }
      this.$onInit = function () {
        const outlet = registerManager.getActiveOutlet()
        this.isCashManagementEnabled = cashMovementsManager.isCashManagementEnabled()
        this.openingCashFloatType = CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT
        this.outletTimeZone = outlet && outlet.time_zone
        if (this.isCashManagementEnabled) {
          this._prepareCashDrawerSummaryForCashManagement()
        }

        // Determine if any of the payment types is a cash concealed payment type
        some(this.registerClosure.payments, payment => {
          if (isCashConcealedPaymentType(payment)) {
            this.hasCashConcealedPaymentType = true
            return true
          }
        })
        each(this.registerClosure.payments, payment =>
          this._defaultPaymentTotalCounted(payment)
        )

        // Calculate summary totals, from here on it will be updated on-change
        this.determineSummaryTotals()

        if (vdFeatureManager.get('payment_type_controls')) {
          this.filteredPayments = this._filterPayments(
            this.registerClosure.payments,
            outlet.id
          )
        } else {
          this.filteredPayments = this.registerClosure.payments
        }
      }
    },
  }
}
